import { PaginationMetadata } from './models/pagination';
import { AvailableEquipment, Rental } from './models/rental';
import { StationEvent } from './models/stationEvent';
import Service from './Service';

export interface RentalListFilters {
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
  invoiceStatus?: string;
  rentalStatus?: number;
}

export interface ExtendOptions {
  hourlyRate: number;
  currentPrice: number;
  currentDeposit: number;
  maxHoursByDeposit: number;
  maxHoursByAvailability: number;
  maxHours: number;
}

class RentalService {
  private static URL_BASE = process.env.REACT_APP_URL_API;

  // The backoffice is a trusted ADMIN/OWNER tool, so it requests full customer info
  // (include_pii=true); the API redacts PII only for callers that don't ask (e.g. the MCP).
  private static rentalsUrl(
    scope: 'current' | 'history',
    stationId: number,
    page: number,
    pageSize?: number,
    filters?: RentalListFilters,
  ): string {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('station_id', String(stationId));
    params.set('include_pii', 'true');
    if (pageSize != null) params.set('page_size', String(pageSize));
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    if (filters?.invoiceStatus) params.set('invoice_status', filters.invoiceStatus);
    if (filters?.rentalStatus != null) params.set('rental_status', String(filters.rentalStatus));
    return `${this.URL_BASE}/rental/admin/${scope}?${params.toString()}`;
  }

  private static URL_RENTAL_END = () => `${this.URL_BASE}/rental/admin/end`;

  private static URL_RENTAL_CLOSE = () => `${this.URL_BASE}/rental/admin/close`;

  private static URL_RENTAL = (id: number) => `${this.URL_BASE}/rental/admin/${id}`;

  private static URL_RENTAL_AVAILABLE_EQUIPMENTS = (id: number) => `${this.URL_BASE}/rental/admin/${id}/available-equipments`;


  static getRentalsCurrent(stationId: number, page = 0, pageSize?: number, filters?: RentalListFilters): Promise<{ rentals: Rental[], metadata: PaginationMetadata }> {
    return Service.get(this.rentalsUrl('current', stationId, page, pageSize, filters));
  }

  static getRentalsHistory(stationId: number, page = 0, pageSize?: number, filters?: RentalListFilters): Promise<{ rentals: Rental[], metadata: PaginationMetadata }> {
    return Service.get(this.rentalsUrl('history', stationId, page, pageSize, filters));
  }

  // All rentals overlapping [from, to] (ISO datetimes), every status included —
  // feeds the per-lock timeline where finished rentals must stay visible.
  static getRentalsRange(stationId: number, from: string, to: string): Promise<{ rentals: Rental[] }> {
    const params = new URLSearchParams({
      station_id: String(stationId),
      from,
      to,
      include_pii: 'true',
    });
    return Service.get(`${this.URL_BASE}/rental/admin/range?${params.toString()}`);
  }

  static getRental(id: number): Promise<{ rental: Rental }> {
    return Service.get(`${this.URL_RENTAL(id)}?include_pii=true`);
  }

  // Station events (keypad codes, lock opens, hook states) tied to the rental
  static getRentalStationEvents(id: number): Promise<{ events: StationEvent[] }> {
    return Service.get(`${this.URL_RENTAL(id)}/station-events`);
  }

  static endRental(id: number): Promise<{ success: boolean }> {
    return Service.post(this.URL_RENTAL_END(), {
      rentalId: id,
    });
  }

  static closeRental(id: number): Promise<{ success: boolean }> {
    return Service.post(this.URL_RENTAL_CLOSE(), {
      rentalId: id,
    });
  }

  static updateRentalStartDateUTC(id: number, startDateUTC: string): Promise<Rental> {
    return Service.patch(this.URL_RENTAL(id), { startDateUTC });
  }

  // `updatePrice` opts into recomputing the rental's price/deposit for the new
  // equipment (only possible for NOT_STARTED, Stripe pre-authorized rentals);
  // omitted/false preserves the rental's current price, as before.
  static changeRentalEquipment(id: number, previousEquipmentId: number, newEquipmentId: number, updatePrice?: boolean): Promise<Rental> {
    return Service.patch(this.URL_RENTAL(id), { previousEquipmentId, newEquipmentId, updatePrice });
  }

  static getAvailableEquipments(rentalId: number): Promise<{ equipments: AvailableEquipment[] }> {
    return Service.get(this.URL_RENTAL_AVAILABLE_EQUIPMENTS(rentalId));
  }

  // Creates a manual/cash rental (reserves the lock's equipment + issues the
  // code immediately). `price`/`deposit` are in cents. Returns the generated
  // code(s) so the modal can display them.
  static createManual(data: {
    lockIds: number[];
    startDate?: string | null;
    duration: number;
    durationUnit: 'hours' | 'days';
    price?: number;
    deposit?: number;
    userId?: number;
    customer?: { firstName?: string; lastName?: string; phone?: string; email?: string };
  }): Promise<{ rental: Rental; codes: { lockId: number | null; token: string }[] }> {
    return Service.post(`${this.URL_BASE}/rental/admin/manual`, data);
  }

  // Records an offline cash payment and closes the rental (no Stripe capture).
  // `amount` is in cents; `includeDeposit` also records the deposit as charged.
  static chargeCashAndClose(id: number, data: { amount: number; includeDeposit: boolean }): Promise<{ success: boolean }> {
    return Service.post(`${this.URL_BASE}/rental/admin/${id}/capture`, {
      amount: data.amount,
      includeDeposit: data.includeDeposit,
    });
  }

  // How far a rental can be extended right now — hourlyRate plus maxHours, already bounded
  // by both the Stripe pre-authorization headroom and equipment availability.
  static getExtendOptions(id: number): Promise<ExtendOptions> {
    return Service.get(`${this.URL_BASE}/rental/admin/${id}/extend-options`);
  }

  // Extends a Stripe pre-authorized rental by `additionalHours`, priced at the plain hourly
  // rate. No new Stripe charge — the higher price is captured later out of the existing
  // pre-authorization (and the same amount is moved out of the deposit into the price).
  static extendRental(id: number, additionalHours: number): Promise<Rental> {
    return Service.post(`${this.URL_BASE}/rental/admin/${id}/extend`, { additionalHours });
  }
}

export default RentalService;
